import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { featureFlags } from "../../config/featureFlags";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import { GovernanceListingSection } from "../../components/governance/GovernanceListingSection";
import { ComplianceCenterMetricTile } from "../../components/compliance-center/ComplianceCenterMetricTile";
import { UnifiedRemediationRegistry } from "../../components/compliance-center/UnifiedRemediationRegistry";
import { Button } from "../../components/ui/Button";
import {
  fetchUnifiedRemediationRegistry,
  type UnifiedRemediationAuthorityPayload,
  type UnifiedRemediationItem,
} from "../../services/unifiedRemediationApi";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";
import { normalizeComplianceStandardCode } from "../../utils/complianceStandard";
import { getActiveOrgId } from "../../utils/orgUtils";
import { buildCloudRequestUrl } from "../../components/integrations/gcpDiscoverySession.mjs";

const RA = "complianceCenterUi.remediationAuthority";

export default function ComplianceCenterRemediationWorkspace() {
  const { t } = useI18n();
  const { token } = useAuth();
  const [selectedStandard, setSelectedStandard] = useState(() =>
    normalizeComplianceStandardCode(localStorage.getItem("ccSelectedStandard")),
  );
  const [loading, setLoading] = useState(true);
  const [authority, setAuthority] = useState<UnifiedRemediationAuthorityPayload | null>(null);
  const [legacyRecommendations, setLegacyRecommendations] = useState<unknown[]>([]);
  const [legacyTasks, setLegacyTasks] = useState<unknown[]>([]);
  const [legacyAnalytics, setLegacyAnalytics] = useState<Record<string, unknown>>({});
  const [aiPlan, setAiPlan] = useState<Record<string, unknown> | null>(null);
  const [cloudEvidence, setCloudEvidence] = useState<Array<{ id: string; ruleId: string; recommendationId?: string | null }>>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [crossStandardImpactByControl, setCrossStandardImpactByControl] = useState<
    Record<string, { totalReadinessGain?: number; impacts?: Array<{ standardCode?: string; readinessGain?: number }> }>
  >({});

  const useAuthority = featureFlags.UNIFIED_REMEDIATION_AUTHORITY_V1;

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === "ccSelectedStandard") {
        setSelectedStandard(normalizeComplianceStandardCode(event.newValue));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    let active = true;
    const load = () => {
      const orgId = getActiveOrgId();
      if (!token || !orgId) {
        setCloudEvidence([]);
        return;
      }
      void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || "/api", "/check-evidence", orgId), {
        headers: { Authorization: `Bearer ${token}` },
      }).then(async (response) => {
        if (!active || getActiveOrgId() !== orgId) return;
        const body = await response.json().catch(() => ({}));
        const rows = response.ok && Array.isArray(body.evidence) ? body.evidence : [];
        setCloudEvidence(rows.filter((row: { recommendationId?: string | null }) => row.recommendationId));
      }).catch(() => {
        if (active && getActiveOrgId() === orgId) setCloudEvidence([]);
      });
    };
    load();
    window.addEventListener("ds-active-organization-changed", load);
    return () => {
      active = false;
      window.removeEventListener("ds-active-organization-changed", load);
    };
  }, [token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      const headers = { Authorization: `Bearer ${token}` };

      if (useAuthority) {
        try {
          const data = await fetchUnifiedRemediationRegistry(token || "", selectedStandard);
          setAuthority(data);
        } catch (err) {
          setLoadError(err instanceof Error ? err.message : "Failed to load remediation registry");
          setAuthority(null);
        }
        try {
        const summaryRes = await fetch(
          `${API_ENDPOINTS.GRC_EXECUTIVE_SUMMARY}?standardCode=${encodeURIComponent(selectedStandard)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
          if (summaryRes.ok) {
            const summaryJson = await summaryRes.json();
            setCrossStandardImpactByControl(
              summaryJson?.data?.crossStandardImpactSummary?.impactByControl || {},
            );
          }
        } catch {
          setCrossStandardImpactByControl({});
        }
        setLoading(false);
        return;
      }

      const [recsRes, tasksRes, analyticsRes] = await Promise.all([
        fetch(API_ENDPOINTS.COMPLIANCE_RECOMMENDATIONS, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_REMEDIATION_TASKS, { headers }),
        fetch(API_ENDPOINTS.COMPLIANCE_REMEDIATION_ANALYTICS, { headers }),
      ]);
      const recsJson = recsRes.ok ? await recsRes.json().catch(() => ({})) : {};
      const tasksJson = tasksRes.ok ? await tasksRes.json().catch(() => ({})) : {};
      const analyticsJson = analyticsRes.ok ? await analyticsRes.json().catch(() => ({})) : {};
      setLegacyRecommendations(Array.isArray(recsJson?.data) ? recsJson.data : []);
      setLegacyTasks(Array.isArray(tasksJson?.data) ? tasksJson.data : []);
      setLegacyAnalytics(analyticsJson?.data || {});
      setLoading(false);
    };
    void load();
  }, [token, useAuthority, selectedStandard]);

  const metrics = authority?.metrics;
  const items: UnifiedRemediationItem[] = authority?.remediationItems || [];

  const criticalOpen = useAuthority
    ? metrics?.highPriorityOpen ?? metrics?.criticalOpen ?? 0
    : legacyRecommendations.filter(
        (item) =>
          String((item as { severity?: string })?.severity || "").toLowerCase() === "critical" &&
          String((item as { status?: string })?.status || "").toLowerCase() !== "completed",
      ).length;

  const overdue = useAuthority
    ? metrics?.overdue ?? 0
    : legacyTasks.filter((item) => Boolean((item as { isOverdue?: boolean })?.isOverdue)).length;

  const blocked = useAuthority
    ? metrics?.blocked ?? 0
    : legacyTasks.filter(
        (item) => String((item as { status?: string })?.status || "").toLowerCase() === "blocked",
      ).length;

  const velocity = useAuthority
    ? metrics?.velocity ?? 0
    : Number(legacyAnalytics?.velocity || 0);

  const openFindings = useAuthority ? metrics?.openFindings ?? 0 : 0;
  const openRecommendations = useAuthority ? metrics?.openRecommendations ?? 0 : 0;
  const complianceObservations = useAuthority ? metrics?.complianceObservations ?? 0 : 0;
  const openRegistryItems = useAuthority ? metrics?.openRegistryItems ?? metrics?.totalOpen ?? 0 : 0;

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={
          useAuthority
            ? t(`${RA}.pageTitle`)
            : t("complianceCenterUi.nav.remediation")
        }
        description={useAuthority ? t(`${RA}.pageIntro`) : undefined}
        toolbar={
          featureFlags.AI_REMEDIATION_ASSISTANT_V1 ? (
            <Button
              type="button"
              size="small"
              color="brand"
              onClick={async () => {
                const res = await fetch(API_ENDPOINTS.AI_REMEDIATION_PLAN, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                  body: JSON.stringify({ standardCode: "ALL" }),
                });
                const json = res.ok ? await res.json().catch(() => ({})) : {};
                setAiPlan(json?.data || null);
              }}
            >
              {t("complianceCenterUi.generateAiPlan")}
            </Button>
          ) : undefined
        }
      >
        {loadError ? (
          <p className="text-sm text-red-600 m-0">{loadError}</p>
        ) : null}
        {loading ? (
          <p className="text-sm text-slate-500 m-0">{t("common.loading")}</p>
        ) : (
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid6}`}>
            <ComplianceCenterMetricTile
              label={t(`${RA}.criticalOpen`)}
              value={criticalOpen}
              tone="red"
            />
            <ComplianceCenterMetricTile
              label={t(`${RA}.overdueTasks`)}
              value={overdue}
              tone="orange"
            />
            <ComplianceCenterMetricTile
              label={t(`${RA}.blockedTasks`)}
              value={blocked}
              tone="yellow"
            />
            <ComplianceCenterMetricTile
              label={t(`${RA}.remediationVelocity`)}
              value={velocity}
              tone="green"
            />
            {useAuthority ? (
              <>
                <ComplianceCenterMetricTile
                  label={t(`${RA}.openFindings`)}
                  value={openFindings}
                  tone="purple"
                />
                <ComplianceCenterMetricTile
                  label={t(`${RA}.openRecommendations`)}
                  value={openRecommendations}
                  tone="blue"
                />
                <ComplianceCenterMetricTile
                  label={t(`${RA}.complianceObservations`, "Compliance observations")}
                  value={complianceObservations}
                  tone="neutral"
                />
                <ComplianceCenterMetricTile
                  label={t(`${RA}.openRegistryItems`, "Open remediation items")}
                  value={openRegistryItems}
                  tone="neutral"
                />
              </>
            ) : null}
          </div>
        )}
      </GovernanceListingSection>

      {useAuthority ? (
        <GovernanceListingSection title={t(`${RA}.registryTitle`)}>
          <UnifiedRemediationRegistry
            items={items}
            loading={loading}
            crossStandardImpactByControl={crossStandardImpactByControl}
          />
        </GovernanceListingSection>
      ) : null}

      <GovernanceListingSection
        title={t("gcpEvidence.remediationTitle", "Configuration remediation")}
        description={t("gcpEvidence.remediationIntro", "These actions are linked to configuration evidence. They do not change the cloud.")}
      >
        {cloudEvidence.length === 0 ? (
          <p>{t("gcpEvidence.remediationEmpty", "No configuration remediation links for this organization.")}</p>
        ) : (
          <ul>
            {cloudEvidence.map((row) => (
              <li key={row.id}>
                {row.ruleId}
                {" · "}
                <Link to="/compliance-center/evidence">{t("gcpEvidence.hub", "Evidence Hub")}</Link>
              </li>
            ))}
          </ul>
        )}
      </GovernanceListingSection>
      {aiPlan ? (
        <GovernanceListingSection title={t("complianceCenterUi.aiRemediationPlan")}>
          <p className="text-sm text-slate-700 m-0">
            {t("complianceCenterUi.confidence")}: {String(aiPlan.confidence ?? "n/a")}
          </p>
          <p className="text-xs text-slate-500 mt-2 m-0">
            {String(aiPlan.disclaimer || t("complianceCenterUi.aiDisclaimer"))}
          </p>
        </GovernanceListingSection>
      ) : null}
    </div>
  );
}
